'use client';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useMermaidRenderer } from '@ui/hooks/useMermaidRenderer';
import { normalizeLinkHtml } from '@ui/lib/link-href';
import { twMerge } from 'tailwind-merge';
import DOMPurify from 'isomorphic-dompurify';
import dynamic from 'next/dynamic';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from '@ui/components/ui/carousel';
import AutoHeight from 'embla-carousel-auto-height';

const InstagramEmbed = dynamic(
  () => import('react-social-media-embed').then((module) => module.InstagramEmbed),
  { ssr: false },
);
const Tweet = dynamic(() => import('react-tweet').then((module) => module.Tweet), {
  ssr: false,
});

interface TextWithLinksProps {
  text: string;
  amountOfWords?: number;
  preventClickThrough?: boolean;
  mode?: 'truncated' | 'full' | 'expandable';
  startsTruncated?: boolean;
  className?: string;
  renderEmbedsInTruncatedMode?: boolean;
}

const TextWithLinksComponent: React.FC<TextWithLinksProps> = ({
  text = '',
  amountOfWords = 66,
  preventClickThrough = false,
  mode,
  startsTruncated = false,
  className,
  renderEmbedsInTruncatedMode = true,
}) => {
  const getActualMode = (): 'truncated' | 'full' | 'expandable' => {
    if (mode) return mode;

    if (startsTruncated) {
      return 'expandable';
    } else {
      return 'full';
    }
  };

  const actualMode = getActualMode();
  const [isTruncated, setIsTruncated] = useState(false);
  const [showFullText, setShowFullText] = useState(actualMode === 'full');
  const [beginText, setBeginText] = useState('');
  const [endText, setEndText] = useState('');
  const [processedHtml, setProcessedHtml] = useState('');
  const [instagramElements, setInstagramElements] = useState<
    Array<{ id: string; url: string; index: number }>
  >([]);
  const [youtubeElements, setYoutubeElements] = useState<
    Array<{ id: string; url: string; index: number }>
  >([]);
  const [truncatedEmbeds, setTruncatedEmbeds] = useState<
    Array<{ id: string; url: string; index: number; type: 'instagram' | 'youtube' | 'twitter' }>
  >([]);
  const [twitterElements, setTwitterElements] = useState<
    Array<{ id: string; url: string; index: number }>
  >([]);
  const contentRef = useRef<HTMLDivElement>(null);
  const [embedCarouselApi, setEmbedCarouselApi] = useState<CarouselApi | undefined>(undefined);

  const embedCarouselContentRef = useRef<HTMLDivElement>(null);
  const [activeEmbedId, setActiveEmbedId] = useState<string | undefined>(undefined);
  const isYoutubeActive = React.useMemo(() => {
    return (
      typeof activeEmbedId === 'string' &&
      (activeEmbedId.toLowerCase().includes('youtube') ||
        activeEmbedId.toLowerCase().includes('youtube-placeholder'))
    );
  }, [activeEmbedId]);
  const isTwitterActive = React.useMemo(() => {
    return (
      typeof activeEmbedId === 'string' &&
      (activeEmbedId.toLowerCase().includes('twitter') ||
        activeEmbedId.toLowerCase().includes('twitter-placeholder'))
    );
  }, [activeEmbedId]);
  const isInstagramActive = React.useMemo(() => {
    return (
      typeof activeEmbedId === 'string' &&
      (activeEmbedId.toLowerCase().includes('instagram') ||
        activeEmbedId.toLowerCase().includes('instagram-placeholder'))
    );
  }, [activeEmbedId]);

  const handleEmbedSlidesChanged = useCallback((api: CarouselApi) => {
    if (!api) return;
    const selectedIndex = api.selectedScrollSnap();
    const nodes = api.slideNodes();
    const node = nodes[selectedIndex];
    const id = node?.id;
    setActiveEmbedId(id);
    if (embedCarouselContentRef.current) {
      embedCarouselContentRef.current.dataset.activeId = id || '';
    }
  }, []);

  // Utility: remove embeds and tidy empty nodes/whitespace to avoid visual gaps
  const stripEmbeds = useCallback((html: string): string => {
    return html
      .replace(/<div[^>]*data-instagram-url[^>]*>.*?<\/div>/gi, '')
      .replace(/<div[^>]*data-youtube-url[^>]*>.*?<\/div>/gi, '')
      .replace(/<div[^>]*data-youtube-video[^>]*>.*?<\/div>/gi, '')
      .replace(/<div[^>]*data-youtube-video[^>]*\/>/gi, '')
      .replace(/<div[^>]*data-twitter-url[^>]*>.*?<\/div>/gi, '');
  }, []);

  const tidyHtml = useCallback((html: string): string => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Remove empty paragraphs/divs/spans, but preserve single line breaks
      const candidates = doc.body.querySelectorAll('p, div, span');
      candidates.forEach((el) => {
        // Preserve if contains media/links
        if (el.querySelector('img, iframe, video, a')) return;

        const text = (el.textContent || '').replace(/\u00A0/g, ' ').trim();
        const onlyBreaks = Array.from(el.childNodes).every((n) => {
          if (n.nodeType === 3) return (n.textContent || '').trim() === '';
          if (n.nodeType === 1) return (n as Element).tagName.toLowerCase() === 'br';
          return true;
        });

        // If element is completely empty, remove
        if (text === '' && el.childElementCount === 0 && !onlyBreaks) {
          el.remove();
          return;
        }

        // If element contains only <br> tags, compress to a single line break
        if (onlyBreaks) {
          const br = doc.createElement('br');
          el.replaceWith(br);
          return;
        }
      });

      // Trim leading/trailing whitespace text nodes
      while (
        doc.body.firstChild &&
        doc.body.firstChild.nodeType === 3 &&
        (doc.body.firstChild.textContent || '').trim() === ''
      ) {
        doc.body.removeChild(doc.body.firstChild);
      }
      while (
        doc.body.lastChild &&
        doc.body.lastChild.nodeType === 3 &&
        (doc.body.lastChild.textContent || '').trim() === ''
      ) {
        doc.body.removeChild(doc.body.lastChild);
      }

      // Remove stray src attributes on non-media elements
      const elementsWithSrc = doc.body.querySelectorAll('[src]');
      elementsWithSrc.forEach((el) => {
        const tag = (el as Element).tagName.toLowerCase();
        if (
          tag !== 'img' &&
          tag !== 'iframe' &&
          tag !== 'video' &&
          tag !== 'audio' &&
          tag !== 'source'
        ) {
          el.removeAttribute('src');
        }
      });

      return doc.body.innerHTML.trim();
    } catch (_e) {
      return html.trim();
    }
  }, []);

  // Tidy truncated previews while keeping blank-line spacing so they match the expanded view.
  const tidyHtmlForTruncated = useCallback((html: string): string => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const candidates = doc.body.querySelectorAll('p, div, span');
      candidates.forEach((el) => {
        if (el.querySelector('img, iframe, video, a, br')) return;

        const text = (el.textContent || '').replace(/\u00A0/g, ' ').trim();

        // Strip only empty inline leftovers; keep empty <p>/<div> and <br> (intentional blank lines).
        const isInline = el.tagName.toLowerCase() === 'span';
        if (text === '' && el.childElementCount === 0 && isInline) {
          el.remove();
        }
      });

      // Remove leading/trailing whitespace text nodes
      while (
        doc.body.firstChild &&
        doc.body.firstChild.nodeType === 3 &&
        (doc.body.firstChild.textContent || '').trim() === ''
      ) {
        doc.body.removeChild(doc.body.firstChild);
      }
      while (
        doc.body.lastChild &&
        doc.body.lastChild.nodeType === 3 &&
        (doc.body.lastChild.textContent || '').trim() === ''
      ) {
        doc.body.removeChild(doc.body.lastChild);
      }

      let output = doc.body.innerHTML.trim();
      // Cap runaway <br> runs at one blank line, but keep normal spacing (a blank line = two <br>).
      output = output.replace(/(?:<br\s*\/?>(?:\s|&nbsp;)*){3,}/gi, '<br/><br/>');
      // Trim leading/trailing <br>
      output = output
        .replace(/^(?:\s|&nbsp;|<br\s*\/?>)+/i, '')
        .replace(/(?:\s|&nbsp;|<br\s*\/?>)+$/i, '');
      return output;
    } catch (_e) {
      // Fallback: cap runaway <br> runs in string form (keep single blank lines)
      return html
        .replace(/(?:<br\s*\/?>(?:\s|&nbsp;)*){3,}/gi, '<br/><br/>')
        .replace(/^(?:\s|&nbsp;|<br\s*\/?>)+/i, '')
        .replace(/(?:\s|&nbsp;|<br\s*\/?>)+$/i, '');
    }
  }, []);

  // Add DOMPurify configuration constant
  const SANITIZE_CONFIG = {
    FORBID_TAGS: ['script', 'style', 'form', 'input', 'button', 'object', 'embed', 'base'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onfocus', 'onblur'],
    ALLOWED_ATTR: [
      'href',
      'target',
      'rel',
      'class',
      'style',
      'src',
      'alt',
      'title',
      'width',
      'height',
      'frameborder',
      'allowfullscreen',
      'allow',
      'data-instagram-url',
      'data-youtube-url',
      'data-youtube-video',
      'data-placeholder',
      'data-type',
      'data-code',
      'data-mention-suggestion-char',
      'id',
      'autoplay',
      'disablekbcontrols',
      'enableiframeapi',
      'endtime',
      'ivloadpolicy',
      'loop',
      'modestbranding',
      'origin',
      'playlist',
      'start',
      'data-twitter-url',
      'colspan',
      'rowspan',
    ],
    ALLOWED_TAGS: [
      'a',
      'p',
      'span',
      'div',
      'iframe',
      'br',
      'strong',
      'em',
      'b',
      'i',
      'u',
      's',
      'code',
      'pre',
      'blockquote',
      'ul',
      'ol',
      'li',
      'img',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'colgroup',
      'col',
    ],
  };

  const processContentForEmbeds = useCallback(
    (
      html: string,
    ): {
      processedHtml: string;
      instagramElements: Array<{ id: string; url: string; index: number }>;
      youtubeElements: Array<{ id: string; url: string; index: number }>;
      twitterElements: Array<{ id: string; url: string; index: number }>;
    } => {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Intentionally process using a combined query below to preserve original embed order
        const instagramElements: Array<{ id: string; url: string; index: number }> = [];
        const youtubeElements: Array<{ id: string; url: string; index: number }> = [];
        const twitterElements: Array<{ id: string; url: string; index: number }> = [];

        let globalIndex = 0;

        const allEmbeds = Array.from(
          doc.querySelectorAll(
            'div[data-instagram-url], div[data-youtube-url], div[data-youtube-video], div[data-twitter-url]',
          ),
        );

        allEmbeds.forEach((embed) => {
          const isInstagram = embed.hasAttribute('data-instagram-url');
          const isYoutube =
            embed.hasAttribute('data-youtube-url') || embed.hasAttribute('data-youtube-video');
          const isTwitter = embed.hasAttribute('data-twitter-url');

          if (isInstagram) {
            const url = embed.getAttribute('data-instagram-url');
            if (url) {
              const placeholderId = `instagram-placeholder-${globalIndex}`;
              instagramElements.push({ id: placeholderId, url, index: globalIndex });

              const placeholder = doc.createElement('div');
              placeholder.id = placeholderId;
              placeholder.setAttribute('data-placeholder', 'instagram');

              embed.parentNode?.replaceChild(placeholder, embed);
              globalIndex++;
            }
          } else if (isYoutube) {
            let url = embed.getAttribute('data-youtube-url');

            // If no data-youtube-url, try to extract from iframe src
            if (!url) {
              const iframe = embed.querySelector('iframe');
              if (iframe) {
                const src = iframe.getAttribute('src');
                if (src && src.includes('youtube.com/embed/')) {
                  // Convert embed URL to watch URL
                  const videoId = src.match(/embed\/([^?]+)/)?.[1];
                  if (videoId) {
                    url = `https://www.youtube.com/watch?v=${videoId}`;
                  }
                }
              }
            }

            if (url) {
              const placeholderId = `youtube-placeholder-${globalIndex}`;
              youtubeElements.push({ id: placeholderId, url, index: globalIndex });

              const placeholder = doc.createElement('div');
              placeholder.id = placeholderId;
              placeholder.setAttribute('data-placeholder', 'youtube');

              embed.parentNode?.replaceChild(placeholder, embed);
              globalIndex++;
            }
          } else if (isTwitter) {
            const url = embed.getAttribute('data-twitter-url');
            if (url) {
              const placeholderId = `twitter-placeholder-${globalIndex}`;
              twitterElements.push({ id: placeholderId, url, index: globalIndex });

              const placeholder = doc.createElement('div');
              placeholder.id = placeholderId;
              placeholder.setAttribute('data-placeholder', 'twitter');

              embed.parentNode?.replaceChild(placeholder, embed);
              globalIndex++;
            }
          }
        });

        return {
          processedHtml: doc.body.innerHTML,
          instagramElements,
          youtubeElements,
          twitterElements,
        };
      } catch (error) {
        console.error('Error processing embed content:', error);
        return {
          processedHtml: html,
          instagramElements: [],
          youtubeElements: [],
          twitterElements: [],
        };
      }
    },
    [],
  );

  let renderText = React.useMemo(() => {
    if (!text) return '';

    let normalizedText = text;

    // Process markdown-style links
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let modifiedText = normalizedText.replace(markdownLinkRegex, (match, linkText, url) => {
      let fullUrl = url;
      if (!/^https?:\/\//i.test(fullUrl)) {
        fullUrl = `https://${fullUrl}`;
      }
      return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer" class="text-primary">${linkText}</a>`;
    });

    // Handle already rendered YouTube iframes
    const youtubeIframeRegex =
      /<div[^>]*data-youtube-video[^>]*>.*?<iframe[^>]*src="[^"]*youtube\.com\/embed\/([^"?]+)[^"]*"[^>]*>.*?<\/iframe>.*?<\/div>/gi;
    modifiedText = modifiedText.replace(youtubeIframeRegex, (match, videoId) => {
      return `<div data-youtube-url="https://www.youtube.com/watch?v=${videoId}"></div>`;
    });

    // Temporarily protect existing anchors to avoid wrapping or replacing inside them
    const anchorPlaceholders: string[] = [];
    modifiedText = modifiedText.replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, (m) => {
      const idx = anchorPlaceholders.push(m) - 1;
      return `__ANCHOR_BLOCK_${idx}__`;
    });

    // First, replace provider URLs (not inside anchors) with embed placeholders
    const instagramRegex =
      /(?![^<]*>)(https?:\/\/(?:www\.)?instagram\.com\/[\w\-\.\/\?=&#%]+)(?![^<]*>)/gi;
    modifiedText = modifiedText.replace(instagramRegex, (match) => {
      return `<div data-instagram-url="${match}"></div>`;
    });

    const youtubeRegex =
      /(?![^<]*>)(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)[\w\-]+[^\s<]*)(?![^<]*>)/gi;
    modifiedText = modifiedText.replace(youtubeRegex, (match) => {
      return `<div data-youtube-url="${match}"></div>`;
    });

    const twitterRegex =
      /(?![^<]*>)(https?:\/\/(?:www\.)?x\.com\/[A-Za-z0-9_]{1,15}\/status\/\d+[^\s<]*)(?![^<]*>)/gi;
    modifiedText = modifiedText.replace(twitterRegex, (match) => {
      return `<div data-twitter-url="${match}"></div>`;
    });

    // Then linkify remaining plain URLs (excluding providers we just handled)
    const urlRegex =
      /(?![^<]*>)(https?:\/\/(?!www\.instagram\.com|www\.youtube\.com|youtu\.be|x\.com)[^\s<]+[^<.,:;"')\]\s])/gi;
    modifiedText = modifiedText.replace(urlRegex, (match) => {
      const isEmail = match.includes('@');
      if (isEmail) {
        return `<a href="mailto:${match}">${match}</a>`;
      } else {
        let url = match;
        if (!/^https?:\/\//i.test(url)) {
          url = `https://${url}`;
        }
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary">${match}</a>`;
      }
    });

    // Restore anchors
    modifiedText = modifiedText.replace(/__ANCHOR_BLOCK_(\d+)__/g, (_m, i) =>
      normalizeLinkHtml(anchorPlaceholders[Number(i)]),
    );

    // Preserve author-entered line breaks
    modifiedText = modifiedText.replace(/\n/g, '<br/>');

    return modifiedText;
  }, [text]);

  useEffect(() => {
    // Always process embeds from the full renderText
    const { processedHtml, instagramElements, youtubeElements, twitterElements } =
      processContentForEmbeds(renderText);
    setProcessedHtml(processedHtml);
    setInstagramElements(instagramElements);
    setYoutubeElements(youtubeElements);
    setTwitterElements(twitterElements);
  }, [renderText, processContentForEmbeds]);

  // Render embeds in their original positions when NOT truncated
  useEffect(() => {
    if (
      !contentRef.current ||
      (!instagramElements.length && !youtubeElements.length && !twitterElements.length)
    )
      return;

    const timeoutId = setTimeout(() => {
      // Render Instagram embeds
      instagramElements.forEach(({ id, url }) => {
        const placeholder = contentRef.current?.querySelector(`#${id}`);
        if (placeholder && !placeholder.hasAttribute('data-rendered') && !isTruncated) {
          placeholder.setAttribute('data-rendered', 'true');
          const embedContainer = document.createElement('div');
          placeholder.appendChild(embedContainer);

          try {
            const { createRoot } = require('react-dom/client');
            const root = createRoot(embedContainer);
            root.render(
              <InstagramEmbed url={url} width="100%" style={{ maxWidth: 550 }} captioned />,
            );
          } catch (error) {
            console.error('Failed to render Instagram embed:', error);
            placeholder.innerHTML = `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary">${url}</a>`;
          }
        }
      });

      // Render YouTube embeds
      youtubeElements.forEach(({ id, url }) => {
        const placeholder = contentRef.current?.querySelector(`#${id}`);
        if (placeholder && !placeholder.hasAttribute('data-rendered') && !isTruncated) {
          placeholder.setAttribute('data-rendered', 'true');
          const embedContainer = document.createElement('div');
          placeholder.appendChild(embedContainer);

          try {
            const { createRoot } = require('react-dom/client');
            const root = createRoot(embedContainer);
            root.render(<CustomYouTubeEmbed url={url} />);
          } catch (error) {
            console.error('Failed to render YouTube embed:', error);
            placeholder.innerHTML = `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary">${url}</a>`;
          }
        }
      });

      // Render Twitter (X) embeds
      twitterElements.forEach(({ id, url }) => {
        const placeholder = contentRef.current?.querySelector(`#${id}`);
        if (placeholder && !placeholder.hasAttribute('data-rendered') && !isTruncated) {
          placeholder.setAttribute('data-rendered', 'true');
          const embedContainer = document.createElement('div');
          placeholder.appendChild(embedContainer);

          try {
            const { createRoot } = require('react-dom/client');
            const root = createRoot(embedContainer);
            const match = url.match(/status\/(\d+)/);
            const tweetId = match ? match[1] : undefined;
            if (tweetId) {
              root.render(<Tweet id={tweetId} />);
            } else {
              throw new Error('Invalid X (Twitter) URL');
            }
          } catch (error) {
            console.error('Failed to render Twitter embed:', error);
            placeholder.innerHTML = `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary">${url}</a>`;
          }
        }
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [instagramElements.length, youtubeElements.length, twitterElements.length, isTruncated]);

  // Render mermaid diagrams in read-only view
  useMermaidRenderer(contentRef, [processedHtml, showFullText]);

  useEffect(() => {
    if (!processedHtml) {
      setBeginText('');
      setEndText('');
      setIsTruncated(false);
      setTruncatedEmbeds([]);
      return;
    }

    // Handle different modes
    if (actualMode === 'truncated') {
      // ALWAYS show truncated text when mode is 'truncated'
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = processedHtml;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      const splittedText = textContent.split(' ').filter(Boolean);

      // Remove embeds and aggressively tidy leftover gaps for truncated preview
      const cleanHtml = tidyHtmlForTruncated(stripEmbeds(processedHtml));

      const beginHTML = cleanHtml
        .split(' ')
        .slice(0, amountOfWords - 1)
        .join(' ');
      setBeginText(beginHTML);
      setEndText('');

      // ONLY WHEN TRUNCATED: Extract embeds for bottom rendering
      const { instagramElements, youtubeElements, twitterElements } =
        processContentForEmbeds(renderText);

      // Check if we should render embeds in truncated mode
      if (renderEmbedsInTruncatedMode) {
        // Find the first Instagram, first YouTube, and first Twitter embed in order of appearance
        const allEmbeds = [
          ...instagramElements.map((item) => ({ ...item, type: 'instagram' as const })),
          ...youtubeElements.map((item) => ({ ...item, type: 'youtube' as const })),
          ...twitterElements.map((item) => ({ ...item, type: 'twitter' as const })),
        ].sort((a, b) => a.index - b.index);
        let foundInstagram = false;
        let foundYouTube = false;
        let foundTwitter = false;
        const firstOfEachInOrder: typeof allEmbeds = [];
        for (const embed of allEmbeds) {
          if (embed.type === 'instagram' && !foundInstagram) {
            firstOfEachInOrder.push(embed);
            foundInstagram = true;
          } else if (embed.type === 'youtube' && !foundYouTube) {
            firstOfEachInOrder.push(embed);
            foundYouTube = true;
          } else if (embed.type === 'twitter' && !foundTwitter) {
            firstOfEachInOrder.push(embed);
            foundTwitter = true;
          }
          if (foundInstagram && foundYouTube && foundTwitter) break;
        }
        setTruncatedEmbeds(firstOfEachInOrder);
      } else {
        setTruncatedEmbeds([]);
      }

      setIsTruncated(true);
    } else if (actualMode === 'full') {
      setBeginText(processedHtml);
      setEndText('');
      setIsTruncated(false);
      setTruncatedEmbeds([]);
    } else {
      // expandable mode
      // Count words from original text for accurate truncation detection
      const originalWords = text.split(' ').filter(Boolean);
      const isTruncatedNow = originalWords.length > amountOfWords;

      if (isTruncatedNow) {
        if (!showFullText) {
          // Remove embeds and aggressively tidy leftover gaps for truncated preview
          const cleanHtml = tidyHtmlForTruncated(stripEmbeds(processedHtml));

          const beginHTML = cleanHtml
            .split(' ')
            .slice(0, amountOfWords - 1)
            .join(' ');
          const endHTML = cleanHtml
            .split(' ')
            .slice(amountOfWords - 1)
            .join(' ');

          setBeginText(beginHTML);
          setEndText(endHTML);

          // ONLY WHEN TRUNCATED: Extract embeds for bottom rendering
          const { instagramElements, youtubeElements, twitterElements } =
            processContentForEmbeds(renderText);

          // Combine all embeds and sort by their original index to preserve order
          const allEmbeds = [
            ...instagramElements.map((item) => ({ ...item, type: 'instagram' as const })),
            ...youtubeElements.map((item) => ({ ...item, type: 'youtube' as const })),
            ...twitterElements.map((item) => ({ ...item, type: 'twitter' as const })),
          ].sort((a, b) => a.index - b.index);

          setTruncatedEmbeds(allEmbeds);
        } else {
          // Show full content with embeds in their original positions
          setBeginText(processedHtml);
          setEndText('');
          setTruncatedEmbeds([]);
        }
      } else {
        setBeginText(processedHtml);
        setEndText('');
        setTruncatedEmbeds([]);
      }
      setIsTruncated(isTruncatedNow);
    }
  }, [
    processedHtml,
    amountOfWords,
    showFullText,
    actualMode,
    processContentForEmbeds,
    text,
    renderText,
    renderEmbedsInTruncatedMode,
  ]);

  const handleClick = (e: any) => {
    if (preventClickThrough) {
      e?.preventDefault();
      e?.stopPropagation();
    }

    if (actualMode === 'expandable') {
      setShowFullText(!showFullText);
    }
  };

  return (
    <p className={twMerge('editor-content m-0', className)}>
      <span
        ref={contentRef}
        className="ProseMirror"
        style={{ lineHeight: '1.5', transition: 'max-height 0.3s ease-out' }}>
        <span
          style={{ display: 'block' }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(showFullText ? processedHtml : beginText, SANITIZE_CONFIG),
          }}
        />
        {/* Calculate truncation directly for more reliable detection */}
        {(() => {
          const originalWords = text.split(' ').filter(Boolean);
          const isContentTruncated = originalWords.length > amountOfWords;

          // For expandable mode: show read more/see less button
          if (isContentTruncated && actualMode === 'expandable') {
            return (
              <span className="text-primary cursor-pointer mb-1" onClick={handleClick}>
                {showFullText ? ' see less' : ' read more'}
              </span>
            );
          }

          // For truncated mode: show read more button (but it doesn't expand)
          if (isContentTruncated && actualMode === 'truncated') {
            return <span className="text-primary mb-1 cursor-pointer">read more</span>;
          }

          return null;
        })()}

        {/* ONLY WHEN TRUNCATED: Render embeds at bottom */}
        {isTruncated &&
          !showFullText &&
          truncatedEmbeds &&
          truncatedEmbeds.length > 0 &&
          (actualMode === 'expandable' || actualMode === 'truncated') && (
            <Carousel
              className="w-full"
              opts={{ align: 'start', loop: false }}
              setApi={setEmbedCarouselApi}
              plugins={[AutoHeight()]}
              onSlidesChanged={handleEmbedSlidesChanged}>
              <CarouselContent
                ref={embedCarouselContentRef}
                className={twMerge(
                  'items-start',
                  isYoutubeActive
                    ? ''
                    : isInstagramActive
                    ? '!h-auto !max-h-[991px]'
                    : isTwitterActive
                    ? '!h-auto !max-h-[800px]'
                    : '',
                )}>
                {truncatedEmbeds.map((embed) => (
                  <CarouselItem key={embed.id} className={twMerge('items-start')} id={embed.id}>
                    <div className="w-full flex justify-start items-start">
                      {embed.type === 'instagram' ? (
                        <InstagramEmbed
                          url={embed.url}
                          width="100%"
                          style={{ maxWidth: 550 }}
                          captioned
                        />
                      ) : embed.type === 'youtube' ? (
                        <CustomYouTubeEmbed url={embed.url} />
                      ) : embed.type === 'twitter' ? (
                        (() => {
                          const match = embed.url.match(/status\/(\d+)/);
                          const tweetId = match ? match[1] : undefined;
                          return tweetId ? (
                            <div>
                              <Tweet id={tweetId} />{' '}
                            </div>
                          ) : null;
                        })()
                      ) : null}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              {truncatedEmbeds.length > 1 && (
                <>
                  <CarouselPrevious
                    className="carousel-control !left-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 text-gray-700 border border-gray-200 shadow-md hover:bg-white"
                    type="button"
                    onClickCapture={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      try {
                        embedCarouselApi?.scrollPrev();
                      } catch {}
                    }}
                    onMouseDownCapture={(e) => {
                      e.stopPropagation();
                    }}
                    onPointerDownCapture={(e) => {
                      e.stopPropagation();
                    }}
                    onTouchStartCapture={(e) => {
                      e.stopPropagation();
                    }}
                  />
                  <CarouselNext
                    className="carousel-control !right-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 text-gray-700 border border-gray-200 shadow-md hover:bg-white"
                    type="button"
                    onClickCapture={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      try {
                        embedCarouselApi?.scrollNext();
                      } catch {}
                    }}
                    onMouseDownCapture={(e) => {
                      e.stopPropagation();
                    }}
                    onPointerDownCapture={(e) => {
                      e.stopPropagation();
                    }}
                    onTouchStartCapture={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </>
              )}
            </Carousel>
          )}
      </span>
    </p>
  );
};

export const TextWithLinks = React.memo(TextWithLinksComponent);

const CustomYouTubeEmbed: React.FC<{ url: string }> = ({ url }) => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isIframeReady, setIsIframeReady] = React.useState(false);
  const [currentThumbnail, setCurrentThumbnail] = React.useState<string>('');
  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  const getVideoId = (url: string): string | null => {
    const patterns = [
      /[?&]v=([^&]+)/,
      /youtu\.be\/([^?]+)/,
      /youtube\.com\/shorts\/([^?]+)/,
      /youtube\.com\/embed\/([^?]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const videoId = getVideoId(url);

  const thumbnailUrls = React.useMemo(
    () => ({
      maxres: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
      hq: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
      mq: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
      sd: `https://img.youtube.com/vi/${videoId}/sddefault.jpg`,
    }),
    [videoId],
  );

  React.useEffect(() => {
    if (!videoId) return;

    setCurrentThumbnail(thumbnailUrls.maxres);

    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth < 1280 || img.naturalHeight < 720) {
        setCurrentThumbnail(thumbnailUrls.hq);
      }
    };
    img.onerror = () => {
      setCurrentThumbnail(thumbnailUrls.hq);
    };
    img.src = thumbnailUrls.maxres;
  }, [videoId, thumbnailUrls]);

  React.useEffect(() => {
    if (!isIframeReady || !iframeRef.current) return;

    const checkPlayerReady = () => {
      try {
        const iframe = iframeRef.current;
        if (iframe && iframe.contentWindow) {
          setTimeout(() => {
            setIsLoaded(true);
          }, 1000);
        }
      } catch (error) {
        setTimeout(() => {
          setIsLoaded(true);
        }, 1500);
      }
    };

    checkPlayerReady();
  }, [isIframeReady]);

  if (!videoId) {
    return (
      <div className="p-4 text-center border border-gray-200 rounded bg-gray-50">
        <p className="text-gray-600">Invalid YouTube URL</p>
      </div>
    );
  }

  if (!currentThumbnail) {
    return (
      <div
        data-youtube-embed
        className="relative w-full aspect-video bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-red-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Loading thumbnail...</p>
        </div>
      </div>
    );
  }

  const handleThumbnailError = () => {
    if (currentThumbnail === thumbnailUrls.maxres) {
      setCurrentThumbnail(thumbnailUrls.hq);
    } else if (currentThumbnail === thumbnailUrls.hq) {
      setCurrentThumbnail(thumbnailUrls.mq);
    } else if (currentThumbnail === thumbnailUrls.mq) {
      setCurrentThumbnail(thumbnailUrls.sd);
    }
  };

  const handleIframeLoad = () => {
    setTimeout(() => {
      setIsIframeReady(true);
      setTimeout(() => {
        setIsLoaded(true);
      }, 200);
    }, 500);
  };

  const handleThumbnailClick = () => {
    if (isIframeReady) {
      setIsLoaded(true);
    }
  };

  return (
    <div
      data-youtube-embed
      className="relative w-full overflow-hidden rounded border border-gray-200 bg-black">
      {!isLoaded && currentThumbnail && (
        <div className="relative w-full aspect-video transition-opacity duration-300">
          <img
            src={currentThumbnail}
            alt="YouTube video thumbnail"
            className="w-full h-full object-cover select-none cursor-pointer"
            style={{ imageRendering: 'crisp-edges' }}
            draggable={false}
            onClick={handleThumbnailClick}
            onError={handleThumbnailError}
            onLoad={(e) => {
              const target = e.target as HTMLImageElement;
              if (target.naturalWidth < 480 || target.naturalHeight < 360) {
                if (currentThumbnail === thumbnailUrls.maxres) {
                  setCurrentThumbnail(thumbnailUrls.hq);
                }
              }
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform duration-200">
              <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
          <div className="absolute top-4 right-4 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm">
            {isIframeReady ? 'Ready!' : 'Loading...'}
          </div>
        </div>
      )}

      <div
        className={`w-full aspect-video transition-opacity duration-300 ${
          !isLoaded ? 'opacity-0 absolute inset-0' : 'opacity-100'
        }`}>
        <iframe
          ref={iframeRef}
          src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1&vq=hd1080&enablejsapi=1`}
          title="YouTube video player"
          className="w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          onLoad={handleIframeLoad}
        />
      </div>
    </div>
  );
};
